import { useAuth } from "@/auth/Auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFinancialAudit } from "@/hooks/useFinancialAudit";
import LayoutInside from "@/layout/LayoutInside";
import useCurrencyStore from "@/store/useCurrencyStore";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  BarChart3,
  Calculator,
  CheckCircle2,
  Coins,
  HelpCircle,
  Info,
  Minus,
  Plus,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const distributionColors: Record<string, string> = {
  available: "bg-emerald-500",
  retained: "bg-orange-500",
  toRelease: "bg-blue-500",
  pendingWithdrawal: "bg-purple-500",
  alreadyWithdrawn: "bg-zinc-500",
};

function AnimatedProgress({
  value,
  color,
  delay = 0,
}: {
  value: number;
  color: string;
  delay?: number;
}) {
  const safeValue =
    isNaN(value) || !isFinite(value) ? 0 : Math.max(0, Math.min(100, value));

  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-800">
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${safeValue}%` }}
        transition={{ duration: 0.8, delay, ease: "easeOut" }}
      />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-40 w-full" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-80 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    </div>
  );
}

export default function FinancialAudit() {
  const { t } = useTranslation(["financialAudit", "common"]);
  const { authData } = useAuth();

  const breadcrumbItems = [
    { text: t("common:reports"), link: "/reports" },
    { text: t("financialAudit:title"), link: "/reports/financial-audit" },
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange] = useState<DateRange | undefined>({
    from: new Date(2020, 0, 1),
    to: new Date(),
  });

  const currencyValue = useCurrencyStore((s) => s.currency);
  const currency = typeof currencyValue === "string" ? currencyValue : "BRL";

  const { data, isLoading } = useFinancialAudit({ dateRange, currency });

  const safeNumber = (value: any): number => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const formatCurrency = (value: any) => {
    const safeValue = safeNumber(value);
    const locale = currency === "USD" ? "en-US" : "pt-BR";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(safeValue);
  };

  return (
    <LayoutInside items={breadcrumbItems}>
      <Link to='/reports' className='w-fit block mb-6'>
        <Button
          variant='default'
          className='bg-transparent text-colorText hover:bg-transparent p-0'
        >
          <ArrowLeft size={16} />
          {t("common:back")}
        </Button>
      </Link>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        <motion.div variants={itemVariants}>
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold">
                {t("financialAudit:title")}
              </h1>
              <p className="text-xs md:text-sm text-zinc-400 mt-1 flex flex-wrap items-center gap-1">
                <span>{authData?.name}</span>
                <span className="hidden sm:inline">•</span>
                <span className="block sm:inline w-full sm:w-auto">
                  {authData?.email}
                </span>
                <span className="hidden sm:inline">•</span>
                <span className="block sm:inline w-full sm:w-auto">
                  {t("financialAudit:walletId")}: {authData?.defaultWallet?.id}
                </span>
              </p>
            </div>

            <Card className="p-3 md:p-4">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-400">
                    {t("financialAudit:period")}: {t("financialAudit:allTime")}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <HelpCircle className="h-4 w-4" />
                    {t("financialAudit:howItWorks")}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : data ? (
          <>
            <motion.div variants={itemVariants}>
              <Card className="p-4 md:p-6 bg-[#18181B]">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm text-zinc-400">
                    {t("financialAudit:netBalancePeriod")}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-zinc-500 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs font-semibold mb-2">
                        {t("financialAudit:formulaTooltip.title")}
                      </p>
                      <pre className="text-xs text-zinc-300 font-mono whitespace-pre-line">
                        {t("financialAudit:formulaTooltip.formula")}
                      </pre>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {formatCurrency(data.summary.netBalance)}
                </h2>

                <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20">
                      <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-400">
                        {t("financialAudit:revenue")}
                      </p>
                      <p className="text-sm font-semibold text-emerald-500">
                        {formatCurrency(data.summary.revenue)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                      <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-400">
                        {t("financialAudit:deductions")}
                      </p>
                      <p className="text-sm font-semibold text-red-500">
                        {formatCurrency(data.summary.deductions)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-teal-500/20">
                      <ArrowUpRight className="h-3.5 w-3.5 text-teal-500" />
                    </div>
                    <div>
                      <p className="text-xs text-zinc-400">
                        {t("financialAudit:liquid")}
                      </p>
                      <p className="text-sm font-semibold text-teal-500">
                        {formatCurrency(data.summary.netBalance)}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div variants={itemVariants}>
                <Card className="p-6 bg-[#18181B] h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-base md:text-lg font-semibold">
                      {t("financialAudit:howCalculated")}
                    </h3>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-zinc-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">
                          {t("financialAudit:howCalculatedTooltip")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="space-y-0">
                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20">
                          <Plus className="h-3.5 w-3.5 text-emerald-500" />
                        </div>
                        <span className="text-sm text-zinc-300">
                          {t("financialAudit:totalBilled")}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs font-semibold mb-1">
                              {t("financialAudit:tooltips.totalBilled.title")}
                            </p>
                            <p className="text-xs text-zinc-400 mb-1">
                              {t(
                                "financialAudit:tooltips.totalBilled.description",
                              )}
                            </p>
                            <p className="text-xs text-emerald-400">
                              {t("financialAudit:tooltips.totalBilled.impact")}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm font-medium text-emerald-500">
                        {formatCurrency(data.calculation.totalBilled)}
                      </span>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                          <Minus className="h-3.5 w-3.5 text-red-400" />
                        </div>
                        <span className="text-sm text-zinc-300">
                          {t("financialAudit:refunds")}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs font-semibold mb-1">
                              {t("financialAudit:tooltips.refunds.title")}
                            </p>
                            <p className="text-xs text-zinc-400 mb-1">
                              {t("financialAudit:tooltips.refunds.description")}
                            </p>
                            <p className="text-xs text-red-400">
                              {t("financialAudit:tooltips.refunds.impact")}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm font-medium text-red-400">
                        -{formatCurrency(data.calculation.refunds)}
                      </span>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                          <Minus className="h-3.5 w-3.5 text-red-400" />
                        </div>
                        <span className="text-sm text-zinc-300">
                          {t("financialAudit:chargebackFees")}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs font-semibold mb-1">
                              {t(
                                "financialAudit:tooltips.chargebackFees.title",
                              )}
                            </p>
                            <p className="text-xs text-zinc-400 mb-1">
                              {t(
                                "financialAudit:tooltips.chargebackFees.description",
                              )}
                            </p>
                            <p className="text-xs text-red-400">
                              {t(
                                "financialAudit:tooltips.chargebackFees.impact",
                              )}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm font-medium text-red-400">
                        -{formatCurrency(data.calculation.chargebackFees)}
                      </span>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                          <Minus className="h-3.5 w-3.5 text-red-400" />
                        </div>
                        <span className="text-sm text-zinc-300">
                          {t("financialAudit:processingFees")}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs font-semibold mb-1">
                              {t(
                                "financialAudit:tooltips.processingFees.title",
                              )}
                            </p>
                            <p className="text-xs text-zinc-400 mb-1">
                              {t(
                                "financialAudit:tooltips.processingFees.description",
                              )}
                            </p>
                            <p className="text-xs text-red-400">
                              {t(
                                "financialAudit:tooltips.processingFees.impact",
                              )}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm font-medium text-red-400">
                        -{formatCurrency(data.calculation.processingFees)}
                      </span>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="flex items-center justify-between py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20">
                          <Minus className="h-3.5 w-3.5 text-red-400" />
                        </div>
                        <span className="text-sm text-zinc-300">
                          {t("financialAudit:withdrawalFees")}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs font-semibold mb-1">
                              {t(
                                "financialAudit:tooltips.withdrawalFees.title",
                              )}
                            </p>
                            <p className="text-xs text-zinc-400 mb-1">
                              {t(
                                "financialAudit:tooltips.withdrawalFees.description",
                              )}
                            </p>
                            <p className="text-xs text-red-400">
                              {t(
                                "financialAudit:tooltips.withdrawalFees.impact",
                              )}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-sm font-medium text-red-400">
                        -{formatCurrency(data.calculation.withdrawalFees)}
                      </span>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div className="flex items-center justify-between py-4 mt-2">
                      <span className="text-sm font-semibold text-white">
                        {t("financialAudit:calculatedNetBalance")}
                      </span>
                      <span className="text-lg font-bold text-white">
                        {formatCurrency(data.calculation.calculatedNetBalance)}
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card className="p-4 md:p-6 bg-[#18181B] h-full">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-base md:text-lg font-semibold">
                      {t("financialAudit:whereIsMoney")}
                    </h3>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-zinc-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm">
                          {t("financialAudit:whereIsMoneyTooltip")}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <div className="space-y-4">
                    {data.distribution.map((item, index) => (
                      <div key={item.label} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-3 h-3 rounded-full ${distributionColors[item.label] || "bg-zinc-500"}`}
                            />
                            <span className="text-sm text-zinc-300">
                              {t(`financialAudit:distribution.${item.label}`)}
                            </span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-3.5 w-3.5 text-zinc-500 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">
                                  {t(
                                    `financialAudit:tooltips.distribution.${item.label}`,
                                  )}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-white">
                              {formatCurrency(item.value)}
                            </span>
                            <span className="text-sm text-zinc-500 w-10 text-right">
                              {safeNumber(item.percentage)}%
                            </span>
                          </div>
                        </div>
                        <AnimatedProgress
                          value={item.percentage}
                          color={
                            distributionColors[item.label] || "bg-zinc-500"
                          }
                          delay={index * 0.1}
                        />
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            </div>
          </>
        ) : null}
      </motion.div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-sm:w-[calc(100%-16px)] sm:w-[calc(100vw-2rem)] max-w-2xl max-h-[85vh] overflow-hidden p-0">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col max-h-[85vh]"
            >
              <DialogHeader className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-colorBorder shrink-0">
                <div className="flex items-center gap-3 pr-6 sm:pr-8">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 border border-primary/20">
                    <Calculator size={20} className="text-primary" />
                  </div>
                  <div>
                    <DialogTitle className="text-base sm:text-xl font-semibold">
                      {t("financialAudit:howItWorksModal.title")}
                    </DialogTitle>
                    <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
                      {t("financialAudit:howItWorksModal.subtitle")}
                    </p>
                  </div>
                </div>
              </DialogHeader>

              <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4 sm:space-y-5">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3"
                >
                  <div className="bg-zinc-900/50 rounded-lg p-3 border border-colorBorder">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <BarChart3 size={14} className="text-emerald-500" />
                      </div>
                      <p className="text-colorText text-xs sm:text-sm font-medium">
                        {t("financialAudit:howCalculated")}
                      </p>
                    </div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                      {t("financialAudit:howItWorksModal.section1Text1")}
                    </p>
                  </div>

                  <div className="bg-zinc-900/50 rounded-lg p-3 border border-colorBorder">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-sky-500/10 border border-sky-500/20">
                        <Coins size={14} className="text-sky-400" />
                      </div>
                      <p className="text-colorText text-xs sm:text-sm font-medium">
                        {t("financialAudit:whereIsMoney")}
                      </p>
                    </div>
                    <p className="text-muted-foreground text-[10px] sm:text-xs leading-relaxed">
                      {t("financialAudit:howItWorksModal.section1Text2")}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 }}
                  className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
                >
                  <h3 className="text-xs sm:text-sm font-semibold text-colorText mb-3 flex items-center gap-2">
                    <Calculator size={14} className="text-primary" />
                    {t("financialAudit:howItWorksModal.section2Title")}
                  </h3>
                  <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                    <pre className="text-[10px] sm:text-xs text-[#A9D342] font-mono whitespace-pre-line leading-relaxed">
                      {t("financialAudit:howItWorksModal.section2Formula")}
                    </pre>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
                >
                  <h3 className="text-xs sm:text-sm font-semibold text-colorText mb-3 flex items-center gap-2">
                    <HelpCircle size={14} className="text-primary" />
                    {t("financialAudit:howItWorksModal.section3Title")}
                  </h3>
                  <div className="space-y-2">
                    <div className="bg-zinc-950/50 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                      <div className="flex items-start gap-2">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 shrink-0 mt-0.5">
                          <CheckCircle2 size={12} className="text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-colorText">
                            {t("financialAudit:howItWorksModal.section3Item1Title")}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                            {t("financialAudit:howItWorksModal.section3Item1Text")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                      <div className="flex items-start gap-2">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 shrink-0 mt-0.5">
                          <Info size={12} className="text-amber-400" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-colorText">
                            {t("financialAudit:howItWorksModal.section3Item2Title")}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                            {t("financialAudit:howItWorksModal.section3Item2Text")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-zinc-950/50 rounded-lg p-2.5 sm:p-3 border border-zinc-800">
                      <div className="flex items-start gap-2">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-red-500/20 shrink-0 mt-0.5">
                          <TrendingDown size={12} className="text-red-400" />
                        </div>
                        <div>
                          <p className="text-xs sm:text-sm font-medium text-colorText">
                            {t("financialAudit:howItWorksModal.section3Item3Title")}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                            {t("financialAudit:howItWorksModal.section3Item3Text")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.25 }}
                  className="bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder"
                >
                  <h3 className="text-xs sm:text-sm font-semibold text-colorText mb-2 flex items-center gap-2">
                    <BarChart3 size={14} className="text-primary" />
                    {t("financialAudit:howItWorksModal.section4Title")}
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                    {t("financialAudit:howItWorksModal.section4Text")}
                  </p>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="p-4 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0"
              >
                <Button onClick={() => setIsModalOpen(false)} className="w-full h-9 sm:h-10 text-xs sm:text-sm">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {t("financialAudit:howItWorksModal.closeButton")}
                </Button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </LayoutInside>
  );
}
